import { forwardRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

// Deep import on purpose: the vendored barrel doesn't export the board
// surface (tapfly's own hosts deep-import BoardCanvas the same way).
import BoardCanvas from "../vendor/liveTapRenderer/components/BoardCanvas";
import { createSymbolImageClient } from "../vendor/liveTapRenderer/lib/symbolImageClient";
import useSceneBoardPoll, { costUsd } from "../liveTap/useSceneBoardPoll";
import useTranscriptFocus from "../liveTap/useTranscriptFocus";
import useArtManifest from "../liveTap/useArtManifest";
import { PaperWait, StyleMismatch, isStyleMismatch } from "./LiveBoardStates";
import ClassicOpening from "./ClassicOpening";
import FullscreenStage from "./FullscreenStage";
import LiveHandoff from "./LiveHandoff";
import LiveTapInfoPanel from "./LiveTapInfoPanel";
import SplitStage from "./SplitStage";
import styles from "./BoardLiveView.module.scss";

/**
 * The "board" live system (TT-1046 Live Tapestry v2): one graphic-recording
 * board that grows through a series of plan versions. The server plans
 * (scene-board archetype "board" keeps every version on the doc); the
 * vendored solver replays the whole series so placements never move
 * retroactively, and the reveal clock is a sentence ordinal.
 *
 * The poll/advance cycle — including the versions delta protocol — lives in
 * useSceneBoardPoll (shared with the scene system). Rendered fully revealed
 * (t=Infinity): the canvas paces each new version's items in as the poll
 * delivers them, and what is already on the board stays as it is (TT-1215).
 * On a closed doc the board is `instant` — a finished meeting reads at a
 * glance, it does not re-letter itself.
 *
 * Until the planner's first version lands, the opening IS classic, as on
 * the scene (TT-1237, Ben's board run: the board's pencil sketch barely
 * showed before a picture chosen from the greetings): the classic canvas on
 * its paper skin, its layout catalog off, while the planner listens for a
 * real window (live.warmup_for); it fades out over the planned board
 * (LiveHandoff). Never a "wait for content" message, and no chip about the
 * board's state either (TT-1168 note 1). The host page owns the playhead
 * (`currentSent`) and the symbol threshold, as it does for classic.
 *
 * What classic and the scene have, the board has too (TT-1168 note 5,
 * TT-1215): the transcript panel beside the board (`infoOpen`, the host's
 * toggle) — in a gutter at the right edge while fullscreen (TT-1174 note
 * 3) — the canvas's PNG export through the ref (`exportPng()`), and
 * context on a click on any line that is a sentence as spoken
 * (useTranscriptFocus).
 */
const BoardLiveView = forwardRef(function BoardLiveView(
  {
    feed,
    resources,
    docTitle,
    docDate,
    segments,
    currentSent,
    currentTurn,
    threshold = 0.5,
    closed = false,
    debug = false,
    infoOpen = false,
    onInfoOpenChange,
    onBoard,
  },
  ref
) {
  const { board, versions: planned, job, jobError, fetchError } = useSceneBoardPoll(feed, "board", { closed });

  useEffect(() => onBoard?.(board), [board, onBoard]);
  const mismatch = isStyleMismatch(board, "board");
  const hasPlanned = !mismatch && !!planned?.length;
  const opening = Boolean(segments?.length) && !mismatch;
  const imageClient = useMemo(() => createSymbolImageClient(resources), [resources]);
  // The board's anchor art and stickers, from the catalog (TT-1310).
  const boardArt = useArtManifest(resources, "board");

  const { focus, onRelease, onSentClick } = useTranscriptFocus(infoOpen, onInfoOpenChange);
  const panel = <LiveTapInfoPanel segments={segments} currentTurn={currentTurn} focus={focus} onRelease={onRelease} />;

  return (
    <div className={styles.boardStage}>
      {(jobError || fetchError) && <div className={styles.errorBanner}>{jobError || fetchError}</div>}
      {mismatch && <StyleMismatch archetype={board.archetype} />}
      {hasPlanned || opening ? (
        <SplitStage infoOpen={infoOpen} panel={panel}>
          {({ aside, onFullscreenChange }) => (
            <FullscreenStage aside={aside} onChange={onFullscreenChange}>
              <LiveHandoff
                up={hasPlanned}
                opening={
                  opening ? (
                    <ClassicOpening
                      segments={segments}
                      currentSent={currentSent}
                      threshold={threshold}
                      docTitle={docTitle}
                      docDate={docDate}
                      resources={resources}
                      imageClient={imageClient}
                    />
                  ) : null
                }
              >
                {hasPlanned && (
                  <div className={styles.canvasFrame}>
                    <BoardCanvas
                      ref={ref}
                      artManifest={boardArt}
                      versions={planned}
                      t={Infinity}
                      instant={closed}
                      onSentClick={onSentClick}
                      meetingName={docTitle}
                      meetingDate={docDate}
                    />
                  </div>
                )}
              </LiveHandoff>
            </FullscreenStage>
          )}
        </SplitStage>
      ) : (
        !mismatch && <PaperWait title={docTitle} hint={closed ? "No board was drawn for this meeting." : undefined} />
      )}
      {debug && board && (
        <div className={styles.metaStrip}>
          scene-board · board · upto {board.upto ?? 0} · versions {planned?.length || 0} · calls {board.calls ?? 0} · $
          {costUsd(board).toFixed(4)}
          {job?.status ? ` · job ${job.status}` : ""}
          {!hasPlanned && opening ? " · opening (classic)" : ""}
        </div>
      )}
    </div>
  );
});

BoardLiveView.propTypes = {
  // A live feed client (liveTap/liveFeedClient.js): the host's or a viewer's.
  feed: PropTypes.object,
  // The appback resource adapter: the opening's classic canvas draws from it.
  resources: PropTypes.object,
  docTitle: PropTypes.string,
  docDate: PropTypes.string,
  // The page's transcript segments: the opening letters them, the panel
  // shows them.
  segments: PropTypes.array,
  // The host's playhead and symbol threshold, for the opening canvas.
  currentSent: PropTypes.number,
  threshold: PropTypes.number,
  // The turn under the playhead: what the transcript panel follows.
  currentTurn: PropTypes.number,
  closed: PropTypes.bool,
  debug: PropTypes.bool,
  // The transcript panel beside the board: the host's toggle, and how a
  // click on the board asks for it.
  infoOpen: PropTypes.bool,
  onInfoOpenChange: PropTypes.func,
  onBoard: PropTypes.func,
};

export default BoardLiveView;
